import lightGallery from 'lightgallery';
import lgZoom from 'lightgallery/plugins/zoom';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgFullscreen from 'lightgallery/plugins/fullscreen';

import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-fullscreen.css';

/**
 * Full-screen image viewer, backed by lightGallery in dynamic mode (as v1 was).
 *
 * Bundled from npm rather than loaded from a CDN the way v1 did it: there's a build step
 * now, so pinning the version in the lockfile and shipping it with the hashed assets is
 * strictly better than a runtime third-party fetch.
 *
 * Used under lightGallery's GPLv3 licence -- `licenseKey: 'GPLv3'` is what silences its
 * console notice, and is the correct value for that licence, not a placeholder.
 *
 * v2 feeds it presigned S3 URLs directly. v1 needed a whole blob-fetch/object-URL cache
 * here because its images were auth-only streams; none of that machinery is needed now,
 * and the images are ordinary URLs to lightGallery.
 */

let instance = null;
let container = null;

function destroy() {
    try {
        instance?.destroy();
    } catch {
        // Already torn down (e.g. closed then navigated); nothing to clean up.
    }
    instance = null;
    container?.remove();
    container = null;
}

export function openViewer(images, startIndex = 0) {
    if (!images.length) return;

    // Rebuild per open: the set of images changes with search and collection filters,
    // and dynamic mode takes its items at construction time.
    destroy();

    const index = Math.max(0, Math.min(startIndex, images.length - 1));

    container = document.createElement('div');
    container.style.display = 'none';
    document.body.appendChild(container);

    instance = lightGallery(container, {
        dynamic: true,
        dynamicEl: images.map((img) => ({
            // display_url is the 1600px WebP rendition; fall back to the thumbnail rather
            // than handing lightGallery an empty src if a derivative is missing.
            src: img.display_url ?? img.thumbnail_url ?? '',
            thumb: img.thumbnail_url ?? img.display_url ?? '',
            subHtml: img.caption ? `<h4>${escapeForCaption(img.caption)}</h4>` : '',
        })),
        plugins: [lgZoom, lgThumbnail, lgFullscreen],
        licenseKey: 'GPLv3',
        // Downloading is offered from the gallery tile, which uses a presigned URL with a
        // Content-Disposition filename; lightGallery's own button would fetch the display
        // rendition instead of the original, which is not what "download" should mean here.
        download: false,
        counter: true,
        loop: true,
        // Thumbnails are cheap here (already-generated 300px WebPs) and make a 23-image
        // collection much faster to navigate than arrow-stepping.
        thumbnail: true,
        animateThumb: true,
        zoomFromOrigin: false,
    });

    container.addEventListener('lgAfterClose', destroy, { once: true });

    instance.openGallery(index);
    return destroy;
}

function escapeForCaption(value) {
    return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');
}
